import React, { useState } from 'react';
import { Button, Chip, Box, Typography } from '@mui/material';
import { WarningAmber, CheckCircle } from '@mui/icons-material';
import { isSaintNickAccount, OWNERSHIP_CONTACT_EMAIL } from '@/utils/constants';
import { ClaimStatus } from '@/types/sequence';
import ClaimModal from './ClaimModal';

interface ClaimButtonProps {
  sequenceId: string;
  creatorId: string;
  claimStatus: ClaimStatus;
  className?: string;
}

const ClaimButton: React.FC<ClaimButtonProps> = ({ 
  sequenceId, 
  creatorId, 
  claimStatus,
  className = ''
}) => {
  const [showModal, setShowModal] = useState(false);
  const isSaintNick = isSaintNickAccount(creatorId);
  
  // Don't show anything if this isn't a Saint Nick sequence
  if (!isSaintNick) {
    return null;
  }

  // Show different UI based on claim status
  const renderClaimStatus = () => {
    switch (claimStatus) {
      case 'pending':
        return (
          <Chip 
            icon={<WarningAmber />}
            label="Claim Under Review"
            color="warning"
            variant="outlined"
            size="small"
          />
        );
      
      case 'verified':
        return (
          <Chip 
            icon={<CheckCircle />}
            label="Verified Owner"
            color="success"
            size="small"
          />
        );
      
      case 'unclaimed':
      default:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button 
              variant="outlined"
              size="small"
              onClick={() => setShowModal(true)}
              fullWidth
              sx={{ mb: 1 }}
            >
              Claim This Sequence
            </Button>
            <Typography variant="caption" color="text.secondary">
              This sequence is currently unclaimed. If you are the original creator, 
              you can claim ownership.
            </Typography>
          </Box>
        );
    }
  };

  return (
    <>
      <Box sx={{ mt: 2, p: 2, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        {renderClaimStatus()}
        
        {claimStatus !== 'verified' && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Questions? Contact{' '}
            <Typography 
              component="a" 
              href={`mailto:${OWNERSHIP_CONTACT_EMAIL}`}
              variant="caption"
              color="primary"
              sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {OWNERSHIP_CONTACT_EMAIL}
            </Typography>
          </Typography>
        )}
      </Box>
      
      <ClaimModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        sequenceId={sequenceId}
        onSuccess={() => {
          setShowModal(false);
          // In a real app, we'd refresh the sequence data here
          window.location.reload();
        }}
      />
    </>
  );
};

export default ClaimButton; 